import Phaser from 'phaser';
import { AudioManager } from '../systems/AudioManager';
import { AUDIO_REGISTRY } from '../systems/audioRegistry';
import { createSaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, PALETTE } from '../types';

/**
 * First scene. Confirms the bundled font is really available to the canvas
 * (main.ts already awaited it; this is the belt-and-braces check) and hands off
 * to Preload.
 */
export class Boot extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  preload(): void {
    // Nothing loads here on purpose: Boot must paint instantly.
  }

  create(): void {
    // One SaveManager for the whole game, shared through the registry.
    const save = createSaveManager();
    this.registry.set('save', save);
    this.registry.set('reducedMotion', save.settings().reducedMotion);

    // One AudioManager for the whole game, like the SaveManager. It creates no
    // AudioContext until something actually asks for a sound, and browsers
    // keep that suspended until a real key press unlocks it.
    const audio = new AudioManager();
    audio.applySettings(save.settings());
    this.registry.set(AUDIO_REGISTRY, audio);
    this.game.events.once(Phaser.Core.Events.DESTROY, () => audio.destroy());

    this.cameras.main.setBackgroundColor(PALETTE.sky);

    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Quest Jump', {
        fontFamily: FONT_FAMILY,
        fontSize: '40px',
        color: PALETTE.ice,
      })
      .setOrigin(0.5)
      .setAlpha(0.35);

    void this.whenFontReady().then(() => this.scene.start('Preload'));
  }

  /** Resolves once the webfont is usable, or after a short timeout. */
  private async whenFontReady(): Promise<void> {
    const fonts = document.fonts;
    if (fonts === undefined) return;
    try {
      await Promise.race([
        fonts.load('400 48px "Atkinson Hyperlegible"'),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
    } catch {
      console.warn('[Boot] Atkinson Hyperlegible did not load; falling back to system UI font.');
    }
  }
}
